import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { QuestionObj, QuizObj } from '../types/quiz';

interface PendingChanges {
  questions: QuestionObj[];
  quizzes: QuizObj[];
  categories: string[] | null;
  deletedQuestions: string[];
  deletedQuizzes: string[];
}

export class FirebaseService {
  private static instance: FirebaseService;
  private userId: string | null = null;
  private debounceTimeout: NodeJS.Timeout | null = null;
  private pendingChanges: PendingChanges = {
    questions: [],
    quizzes: [],
    categories: null,
    deletedQuestions: [],
    deletedQuizzes: [],
  };

  private constructor() {
    // Add event listeners for page unload
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
    window.addEventListener('unload', this.handlePageUnload.bind(this));
  }

  private async handlePageUnload(event: BeforeUnloadEvent | Event) {
    // If there are pending changes, show a confirmation dialog
    if (this.hasPendingChanges()) {
      if (event instanceof BeforeUnloadEvent) {
        event.preventDefault();
        event.returnValue = '';
      }

      try {
        // Synchronously flush changes
        await this.flushChanges();
      } catch (error) {
        console.error('Error saving changes before unload:', error);
      }
    }
  }

  private hasPendingChanges(): boolean {
    return (
      this.pendingChanges.questions.length > 0 ||
      this.pendingChanges.quizzes.length > 0 ||
      this.pendingChanges.categories !== null ||
      this.pendingChanges.deletedQuestions.length > 0 ||
      this.pendingChanges.deletedQuizzes.length > 0
    );
  }

  static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  private resetPendingChanges() {
    this.pendingChanges = {
      questions: [],
      quizzes: [],
      categories: null,
      deletedQuestions: [],
      deletedQuizzes: [],
    };
  }

  private async flushChanges() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    if (!this.hasPendingChanges()) return;

    try {
      this.validateUserId();
      const batch = writeBatch(db);

      // Handle questions
      this.pendingChanges.questions.forEach((question) => {
        const docRef = question.id
          ? doc(db, `users/${this.userId}/questions/${question.id}`)
          : doc(collection(db, `users/${this.userId}/questions`));

        if (!question.id) {
          question.id = docRef.id;
        }

        batch.set(docRef, {
          ...question,
          number: question.number || -1,
          quizId: question.quizId || '',
        });
      });

      // Handle quizzes
      this.pendingChanges.quizzes.forEach((quiz) => {
        const docRef = quiz.id
          ? doc(db, `users/${this.userId}/quizzes/${quiz.id}`)
          : doc(collection(db, `users/${this.userId}/quizzes`));

        if (!quiz.id) {
          quiz.id = docRef.id;
        }

        batch.set(docRef, {
          ...quiz,
        });
      });

      // Handle categories
      if (this.pendingChanges.categories !== null) {
        const categoriesRef = doc(
          db,
          `users/${this.userId}/settings`,
          'categories',
        );
        batch.set(categoriesRef, {
          categories: this.pendingChanges.categories,
        });
      }

      // Handle deletions
      this.pendingChanges.deletedQuestions.forEach((questionId) => {
        const questionRef = doc(
          db,
          `users/${this.userId}/questions/${questionId}`,
        );
        batch.delete(questionRef);
      });

      this.pendingChanges.deletedQuizzes.forEach((quizId) => {
        const quizRef = doc(db, `users/${this.userId}/quizzes/${quizId}`);
        batch.delete(quizRef);
      });

      await batch.commit();
      this.resetPendingChanges();
    } catch (error) {
      console.error('Error flushing changes to Firebase:', error);
      throw error;
    }
  }

  private scheduleFlush() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(async () => {
      await this.flushChanges();
      this.debounceTimeout = null;
    }, 1000);
  }

  signOut(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    auth.signOut();
    window.location.reload();
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  private validateUserId(): void {
    if (!this.userId) {
      throw new Error('User ID not set');
    }
  }

  generateId(): string {
    return doc(collection(db, 'temp')).id;
  }

  async saveQuestions(questions: QuestionObj[]): Promise<void> {
    const updatedQuestions = [...this.pendingChanges.questions];

    questions.forEach((newQuestion) => {
      const existingIndex = updatedQuestions.findIndex(
        (q) => q.id === newQuestion.id,
      );
      if (existingIndex !== -1) {
        updatedQuestions[existingIndex] = newQuestion;
      } else {
        updatedQuestions.push(newQuestion);
      }
    });

    this.pendingChanges.questions = updatedQuestions;
    this.scheduleFlush();
  }

  async getQuestions(): Promise<QuestionObj[]> {
    try {
      this.validateUserId();

      const questionsRef = collection(db, `users/${this.userId}/questions`);
      const snapshot = await getDocs(questionsRef);

      return snapshot.docs.map(
        (doc) =>
          new QuestionObj({
            ...doc.data(),
          } as QuestionObj),
      );
    } catch (error) {
      console.error('Error getting questions:', error);
      throw error;
    }
  }

  async deleteQuestion(firebaseId: string): Promise<void> {
    this.pendingChanges.deletedQuestions.push(firebaseId);
    this.scheduleFlush();
  }

  async saveQuizzes(quizzes: QuizObj[]): Promise<void> {
    const updatedQuizzes = [...this.pendingChanges.quizzes];

    quizzes.forEach((newQuiz) => {
      const existingIndex = updatedQuizzes.findIndex(
        (q) => q.id === newQuiz.id,
      );
      if (existingIndex !== -1) {
        updatedQuizzes[existingIndex] = newQuiz;
      } else {
        updatedQuizzes.push(newQuiz);
      }
    });

    this.pendingChanges.quizzes = updatedQuizzes;
    this.scheduleFlush();
  }

  async getQuizzes(): Promise<QuizObj[]> {
    try {
      this.validateUserId();
      const quizzesRef = collection(db, `users/${this.userId}/quizzes`);
      const snapshot = await getDocs(quizzesRef);

      return snapshot.docs.map(
        (doc) =>
          ({
            ...doc.data(),
            lastUpdated: doc.data().lastUpdated.toDate(),
          }) as QuizObj,
      );
    } catch (error) {
      console.error('Error getting quizzes:', error);
      throw error;
    }
  }

  async deleteQuiz(firebaseId: string): Promise<void> {
    this.pendingChanges.deletedQuizzes.push(firebaseId);
    this.scheduleFlush();
  }

  async saveCategories(categories: string[]): Promise<void> {
    this.pendingChanges.categories = categories;
    this.scheduleFlush();
  }

  async getCategories(): Promise<string[]> {
    try {
      this.validateUserId();

      const settingsRef = collection(db, `users/${this.userId}/settings`);
      const categoriesQuery = query(
        settingsRef,
        where('__name__', '==', 'categories'),
      );

      const snapshot = await getDocs(categoriesQuery);

      if (snapshot.empty) {
        return [];
      }

      return snapshot.docs[0].data().categories;
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  }

  async syncToFirebase(
    questions: QuestionObj[],
    quizzes: QuizObj[],
    categories: string[],
  ): Promise<void> {
    try {
      this.validateUserId();

      this.pendingChanges.questions.push(...questions);
      this.pendingChanges.quizzes.push(...quizzes);
      this.pendingChanges.categories = categories;

      await this.flushChanges();
    } catch (error) {
      console.error('Error syncing to Firebase:', error);
      throw error;
    }
  }

  async syncFromFirebase() {
    try {
      this.validateUserId();

      const [questions, quizzes, categories] = await Promise.all([
        this.getQuestions(),
        this.getQuizzes(),
        this.getCategories(),
      ]);

      return { questions, quizzes, categories };
    } catch (error) {
      console.error('Error syncing from Firebase:', error);
      throw error;
    }
  }

  // Clean up event listeners when service is destroyed
  destroy() {
    window.removeEventListener(
      'beforeunload',
      this.handlePageUnload.bind(this),
    );
    window.removeEventListener('unload', this.handlePageUnload.bind(this));
  }
}

export const firebaseService = FirebaseService.getInstance();
